import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import apiService from '../../services/api';
import { renderCountryFlag } from '../../utils/flagUtils';

interface Country {
  id: number;
  name: string;
  name_ar?: string;
  code: string;
  currency: string;
  flag_emoji?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  package_count?: number;
}

const CountryManagement: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    code: '',
    currency: '',
    flag_emoji: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchCountries(), fetchPackageCounts()]);
    setLoading(false);
  };

  const fetchCountries = async () => {
    try {
      const data = await apiService.getAdminCountries();
      if (data.success && data.countries) {
        setCountries(data.countries);
      }
    } catch (error) {    }
  };

  const fetchPackageCounts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/backend/api/admin/packages/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.packages) {
        // Count packages per country
        const packageCounts = data.packages.reduce((counts: Record<number, number>, pkg: any) => {
          counts[pkg.country_id] = (counts[pkg.country_id] || 0) + 1;
          return counts;
        }, {});

        // Update countries with package counts
        setCountries(prevCountries => 
          prevCountries.map(country => ({
            ...country,
            package_count: packageCounts[country.id] || 0
          }))
        );
      }
    } catch (error) {    }
  };

  const openModal = (country: Country | null = null) => {
    if (country) {
      setEditingCountry(country);
      setFormData({
        name: country.name,
        name_ar: country.name_ar || '',
        code: country.code,
        currency: country.currency,
        flag_emoji: country.flag_emoji || '',
        active: country.active
      });
    } else {
      setEditingCountry(null);
      setFormData({
        name: '',
        name_ar: '',
        code: '',
        currency: '',
        flag_emoji: '',
        active: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCountry(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const method = editingCountry ? 'PUT' : 'POST';
      const url = editingCountry 
        ? `/backend/api/admin/countries/?id=${editingCountry.id}`
        : '/backend/api/admin/countries/';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchData();
        closeModal();
        alert(editingCountry ? 'Country updated successfully!' : 'Country created successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to save country'));
      }
    } catch (error) {      alert('Network error occurred');
    }
  };

  const handleDelete = async (countryId: number, packageCount: number) => {
    if (packageCount > 0) {
      alert(`Cannot delete this country because it has ${packageCount} package(s) associated with it. Please remove or reassign the packages first.`);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this country?')) return;

    try {
      const data = await apiService.deleteAdminCountry(countryId);

      if (data.success) {
        await fetchData();
        alert('Country deleted successfully!');
      } else {
        alert('Error: ' + (data.error || 'Failed to delete country'));
      }
    } catch (error) {
      alert('Network error occurred');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" dir="ltr">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Country Management</h2>
          <button
            onClick={() => openModal()}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Add New Country
          </button>
        </div>

        {/* Countries Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Currency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Packages
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {countries.map((country) => (
                <tr key={country.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="mr-2">
                        {renderCountryFlag(
                          country.code,
                          country.flag_emoji || '🌍',
                          country.name,
                          'w-8 h-6 object-cover rounded-sm'
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{country.name}</div>
                        {country.name_ar && (
                          <div className="text-sm text-gray-600" dir="rtl">{country.name_ar}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                      {country.code}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{country.currency}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {country.package_count || 0} packages
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      country.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {country.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openModal(country)}
                      className="text-primary-600 hover:text-primary-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(country.id, country.package_count || 0)}
                      className={`${
                        (country.package_count || 0) > 0
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-red-600 hover:text-red-900'
                      }`}
                      disabled={(country.package_count || 0) > 0}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {countries.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-500">No countries found. Create your first country to get started.</p>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingCountry ? 'Edit Country' : 'Add New Country'}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country Name (English)
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., United States"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country Name (Arabic)
                    </label>
                    <input
                      type="text"
                      name="name_ar"
                      value={formData.name_ar}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., الولايات المتحدة"
                      dir="rtl"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      required
                      maxLength={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., USA"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <input
                      type="text"
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      required
                      maxLength={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., USD"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flag Emoji
                    </label>
                    <input
                      type="text"
                      name="flag_emoji"
                      value={formData.flag_emoji}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., 🇺🇸"
                      maxLength={10}
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Active Country
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
                    >
                      {editingCountry ? 'Update' : 'Create'} Country
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CountryManagement;